/**
 * What the four lenses share (ui/DESIGN.md, "Lenses"): the frame that reveals under the map, the step marks of a
 * record (✓ ◷ ✕ —) with their lead time as visible small text, the age with its freshness dot, a status as a word
 * with a dot, and the calm empty sentence. Colour is a dot, a glyph or text, never a fill.
 */
import type { ReactNode } from 'react';
import dayjs from 'dayjs';
import type { RecordKind, RecordStep, StepCell as Cell } from '../../lib/api.ts';
import { count, duration } from '../../lib/format.ts';
import { age, freshness, type Freshness } from '../../lib/freshness.ts';
import { useNow } from '../../lib/asof.tsx';
import { CloseIcon } from '../../components/icons.tsx';
import { IconButton, Kicker, NounIcon, Surface, SystemBadge, cx, noun, type Noun } from '../../design/primitives.tsx';

/** A stored document, enough to open it (the same shape as components/MessageWindow.tsx's). */
export interface MessageRef { vaultId: string; uuid: string; day?: string }

// ---------------------------------------------------------------- the frame

/**
 * A lens revealed under the map: a stage-level surface that rises in, with a kicker naming the noun, the title
 * ("sales orders", an integration), the count sentence, choosers at the right and a quiet close.
 */
export function LensFrame({ kind, title, count: sentence, right, onClose, className, children }: {
  kind?: Noun; title: ReactNode; count?: ReactNode; right?: ReactNode; onClose?: () => void; className?: string; children?: ReactNode;
}) {
  return (
    <Surface radius="stage" className={cx('mv-rise px-6 py-5 flex flex-col gap-4', className)}>
      <header className="flex items-center gap-x-3 gap-y-2 flex-wrap min-h-8">
        <Kicker className="inline-flex items-center gap-1.5">
          {kind && <NounIcon kind={kind} size={12} />}
          {kind ? `${kind}s` : 'values'}
        </Kicker>
        <span className="text-body font-medium text-n10">{title}</span>
        {sentence && <span className="text-small text-n7">{sentence}</span>}
        {(right || onClose) && (
          <span className="ml-auto flex items-center gap-2 flex-wrap justify-end">
            {right}
            {onClose && <IconButton label="Close" onClick={onClose}><CloseIcon size={14} /></IconButton>}
          </span>
        )}
      </header>
      {children}
    </Surface>
  );
}

/** One calm sentence where a list would be. */
export function EmptyLens({ children }: { children: ReactNode }) {
  return <p className="m-0 py-8 text-center text-body text-n8">{children}</p>;
}

/** A quiet "show 200 more" / "show all 2 000" under a list. */
export function MorePill({ onClick, disabled, children }: { onClick: () => void; disabled?: boolean; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      className="self-center h-7 px-3.5 rounded-full bg-n3 text-n9 text-small font-medium border-0 cursor-pointer whitespace-nowrap transition-colors duration-[var(--t-micro)] ease-mv hover:bg-n4 disabled:opacity-40 disabled:pointer-events-none">
      {children}
    </button>
  );
}

/** The track a few Pills sit in. */
export function PillTrack({ className, children }: { className?: string; children: ReactNode }) {
  return <span className={cx('inline-flex flex-wrap items-center gap-0.5 bg-n3 rounded-full p-0.5', className)}>{children}</span>;
}

// ---------------------------------------------------------------- marks and words

const signed = (ms: number) => (ms < 0 ? `−${duration(-ms)}` : `+${duration(ms)}`);

/** A count with the word for what it counts: the number in mono, the noun in sans ("9 runs", "3 failed"). */
export function Counted({ n, word, className }: { n: number; word: string; className?: string }) {
  return <span className={cx('whitespace-nowrap', className)}><span className="font-mono tabular-nums">{count(n)}</span> {word}</span>;
}

/** The column head of a step: the system's badge and what the step is, faded when not every record gets one. */
export function StepHead({ step }: { step: RecordStep }) {
  return (
    <span className={cx('inline-flex flex-col items-center gap-1', !step.established && 'opacity-50')}>
      <SystemBadge code={step.system} size={20} />
      <span className="text-micro normal-case tracking-normal font-normal text-n7 whitespace-nowrap">{noun(step.cls)}</span>
    </span>
  );
}

/**
 * How far a record got at one step: ✓ with the time after its earliest document (`base`), ✕ when the document
 * came in a failed run (`failed`), ◷ waiting, ✕ overdue, — none. Words are set in sans; only the delta is mono.
 */
export function StepCell({ cell, base, failed }: { cell: Cell; base: number; failed?: boolean }) {
  let mark = '—';
  let tone = 'text-n5';
  let note: string | null = null;
  let mono = false;
  if (cell === 'missing') [mark, tone, note] = ['✕', 'text-fail', 'overdue'];
  else if (cell === 'pending') [mark, tone, note] = ['◷', 'text-warn', 'waiting'];
  else if (cell === 0) [mark, tone, note] = [failed ? '✕' : '✓', failed ? 'text-fail' : 'text-ok', 'earlier'];
  else if (typeof cell === 'number') {
    const delta = Math.max(0, cell - base);
    [mark, tone, note, mono] = [failed ? '✕' : '✓', failed ? 'text-fail' : 'text-ok', delta < 1000 ? 'first' : signed(delta), delta >= 1000];
  }
  return (
    <span className="inline-flex flex-col items-center gap-0.5 leading-none">
      <span className={cx('font-mono text-small leading-none', tone)}>{mark}</span>
      {note && <span className={cx('text-micro leading-none whitespace-nowrap', mono && 'font-mono', failed ? 'text-fail' : 'text-n7')}>{note}</span>}
    </span>
  );
}

const FRESH: Record<Freshness, { dot: string; text: string }> = {
  fresh: { dot: 'bg-ok', text: 'text-n9' },
  aging: { dot: 'bg-warn', text: 'text-n9' },
  stale: { dot: 'bg-n5', text: 'text-n7' },
};

/** How long ago, with a freshness dot when the kind has a usual interval to judge it against. */
export function LastSeen({ last, usual, className }: { last: number | null | undefined; usual?: number | null; className?: string }) {
  const now = useNow();
  if (!last) return <span className={cx('font-mono text-small text-n7', className)}>never</span>;
  const f = freshness(last, usual, now);
  return (
    <span className={cx('inline-flex items-center gap-1.5 font-mono text-small tabular-nums whitespace-nowrap', f ? FRESH[f].text : 'text-n8', className)}>
      {f && <i className={cx('size-1.5 rounded-full shrink-0', FRESH[f].dot)} aria-hidden="true" />}
      {age(last, now)} ago
    </span>
  );
}

const STATUS: Record<string, { word: string; dot: string; text?: string }> = {
  SUCCESS: { word: 'done', dot: 'bg-ok' },
  FAILED: { word: 'failed', dot: 'bg-fail', text: 'text-fail' },
  HOLD: { word: 'on hold', dot: 'bg-warn', text: 'text-warn' },
  NEW: { word: 'new', dot: 'bg-n6' },
  PROCESSING: { word: 'running', dot: 'bg-accent' },
};

/** A status as a word with its dot: done · failed · on hold · new · running. */
export function StatusWord({ status, className }: { status?: string | null; className?: string }) {
  const s = (status && STATUS[status]) || { word: status ? status.toLowerCase() : '—', dot: 'bg-n5' };
  return (
    <span className={cx('inline-flex items-center gap-1.5 text-small whitespace-nowrap', s.text ?? 'text-n9', className)}>
      <i className={cx('size-1.5 rounded-full shrink-0', s.dot)} aria-hidden="true" />{s.word}
    </span>
  );
}

/**
 * The outcome a run ends up with: failed if any document failed, on hold if any is, running if some are still in
 * work, else done — a document stored as "new" is a run's work done, not a status of the run.
 */
export const runStatus = (r: { failed: number; statuses: Record<string, number> }) =>
  r.failed > 0 ? 'FAILED' : r.statuses.HOLD ? 'HOLD' : r.statuses.PROCESSING && !r.statuses.SUCCESS ? 'PROCESSING' : 'SUCCESS';

// ---------------------------------------------------------------- values

const ISO = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2})?)?/;

/** A value in a list: dates as dates, the rest as is. */
export function showValue(v: string | undefined): string {
  if (!v) return '';
  if (ISO.test(v)) {
    const d = dayjs(v);
    if (d.isValid()) return v.length > 10 ? d.format('D.M.YYYY HH:mm') : d.format('D.M.YYYY');
  }
  return v;
}

/** Identifiers, codes and timestamps are set in mono; names and sentences are not. */
export const isIdentifier = (v: string) => ISO.test(v) || (/\d/.test(v) && /^[\w./:+@-]+$/.test(v)) || /^[A-Z0-9_-]{2,}$/.test(v);

/** HH:mm today, otherwise D.M. HH:mm (real today, not the as-of moment: the list is read now). */
export function compact(ms: number, seconds = false): string {
  const d = dayjs(ms);
  const t = seconds ? 'HH:mm:ss' : 'HH:mm';
  return d.isSame(dayjs(), 'day') ? d.format(t) : d.format(`D.M. ${t}`);
}

/** "105", "2 000+": how many records a kind has. */
export const recordsOf = (k: Pick<RecordKind, 'records' | 'more'>) => `${count(k.records)}${k.more ? '+' : ''}`;

/** The count of a noun in a lens header: its icon in its colour, the number in mono, the noun (one plural rule). */
export function NounCount({ kind, n, label, word }: { kind: Noun; n: number; label?: string; word?: string }) {
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      <NounIcon kind={kind} size={12} />
      <span className="font-mono tabular-nums">{label ?? count(n)}</span> {word ?? plural(n, kind)}
    </span>
  );
}

export const plural = (n: number, one: string, many = `${one}s`) => (n === 1 ? one : many);
